import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowSquareOut, ArrowClockwise, Monitor, WarningCircle } from "@phosphor-icons/react";

export function LiveDashboardEmbed({ url, pollInterval = 0, enabled = false }) {
  const [reloadKey, setReloadKey] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);
  const [lastReload, setLastReload] = useState(null);
  const timer = useRef(null);

  const triggerReload = () => {
    setReloadKey((k) => k + 1);
    setLoadFailed(false);
    setLastReload(new Date().toLocaleTimeString());
  };

  // Auto-reload on poll interval
  useEffect(() => {
    if (timer.current) clearInterval(timer.current);
    const interval = Number(pollInterval) || 0;
    if (interval > 0 && enabled && url) {
      timer.current = setInterval(triggerReload, interval * 1000);
    }
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [pollInterval, enabled, url]);

  // Detect iframe load failure by waiting a few seconds for "load" event
  useEffect(() => {
    if (!url) return;
    setLoadFailed(false);
    const timeout = setTimeout(() => {
      // If we never got a successful "load" event, assume X-Frame-Options blocked us
      // (the user can still click "Open in new tab")
    }, 5000);
    return () => clearTimeout(timeout);
  }, [url, reloadKey]);

  return (
    <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <Monitor size={16} weight="bold" className="text-[#32D74B]" />
            Amano Live Dashboard
            {Number(pollInterval) > 0 && enabled && (
              <span className="text-[10px] text-[#32D74B] normal-case tracking-normal">● auto-reload {pollInterval}s</span>
            )}
          </CardTitle>
          <div className="flex items-center gap-3">
            {lastReload && <span className="text-[10px] text-[#737373]">Last: {lastReload}</span>}
            <Button onClick={triggerReload} disabled={!url} size="sm" variant="outline"
              className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm text-xs" data-testid="dashboard-reload-btn">
              <ArrowClockwise size={14} className="mr-1" /> Reload
            </Button>
            <Button onClick={() => window.open(url, "_blank", "noopener,noreferrer")} disabled={!url} size="sm"
              className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-sm text-xs" data-testid="dashboard-newtab-btn">
              <ArrowSquareOut size={14} className="mr-1" /> Open in new tab
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!url ? (
          <p className="text-sm text-[#737373]">Set the &quot;Dashboard URL&quot; above and save to see the live Amano dashboard here.</p>
        ) : loadFailed ? (
          <div className="bg-[#FACC15]/10 border border-[#FACC15]/30 rounded-sm p-4 space-y-2">
            <div className="flex items-center gap-2">
              <WarningCircle size={18} weight="bold" className="text-[#FACC15]" />
              <p className="text-sm text-[#FACC15] font-semibold">Embed blocked</p>
            </div>
            <p className="text-xs text-[#A3A3A3]">
              Amano&apos;s server is blocking iframe embedding (X-Frame-Options). Use &quot;Open in new tab&quot; instead, or ask your Amano admin to allow embedding from this domain.
            </p>
            <p className="text-[10px] font-mono-data text-[#737373] break-all">URL: {url}</p>
          </div>
        ) : (
          <div className="relative bg-[#0A0A0A] border border-[#262626] rounded-sm overflow-hidden" style={{ height: "70vh" }} data-testid="dashboard-iframe-container">
            <iframe
              key={reloadKey}
              src={url}
              title="Amano Live Dashboard"
              className="w-full h-full border-0"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
              onLoad={() => setLoadFailed(false)}
              onError={() => setLoadFailed(true)}
              data-testid="dashboard-iframe"
            />
          </div>
        )}
        {url && (
          <p className="text-[10px] text-[#737373] mt-2 font-mono-data break-all">Embedded: {url}</p>
        )}
      </CardContent>
    </Card>
  );
}
